"use client"

import { ReactNode } from "react"
import { ClerkProvider } from "@clerk/nextjs"
import { DEV_BYPASS_CLERK } from "src/auth/dev-bypass"
import { DevBypassBanner } from "src/auth/dev-bypass-banner"

// Match the pre-Core-3 header copy ("Sign in" / "to continue to ...")
const LOCALIZATION = {
  signIn: {
    start: {
      title: "Sign in",
      subtitle: "to continue to {{applicationName}}",
    },
  },
  signUp: {
    start: {
      title: "Create your account",
      subtitle: "to continue to {{applicationName}}",
    },
  },
}

const ALLOWED_REDIRECT_ORIGINS = [
  "https://*.seam.vc",
  "https://connect.getseam.com",
]

const SATELLITE_CONFIG = {
  domain: "seam.vc",
  // Core 3 no longer auto-syncs satellite sessions on first visit by
  // default; previews on *.seam.vc rely on it.
  satelliteAutoSync: true,
  signInUrl: "https://console.seam.co/signin",
  signUpUrl: "https://console.seam.co/signup",
}

const isSatelliteUrl = (url: URL) => url.hostname.endsWith(".seam.vc")

// Dev-only: when DEV_BYPASS_CLERK is active, don't mount ClerkProvider so the
// app doesn't bounce to the Clerk dev satellite. Pages that call Clerk hooks
// won't work in this mode — that's the trade-off. A visible banner makes sure
// nobody forgets they're in bypass mode.
export function MaybeClerkProvider({ children }: { children: ReactNode }) {
  if (DEV_BYPASS_CLERK) {
    return (
      <>
        {children}
        <DevBypassBanner />
      </>
    )
  }

  const isSatelliteHost =
    typeof window !== "undefined" &&
    window.location.hostname.endsWith(".seam.vc")

  return (
    <ClerkProvider
      localization={LOCALIZATION}
      allowedRedirectOrigins={ALLOWED_REDIRECT_ORIGINS}
      isSatellite={isSatelliteUrl}
      {...(isSatelliteHost ? SATELLITE_CONFIG : {})}
    >
      {children}
    </ClerkProvider>
  )
}
